import lexer from 'js-tokens';
import type { ModuleDeclaration } from '~/lib/static-analysis/esm';
import MagicString from '~/lib/static-analysis/magic';
import { renderESM } from '~/lib/static-analysis/render-esm';
import { workbenchStore } from '~/lib/stores/workbench';
import { stripIndents } from '~/utils/stripIndent';
import { addWorkDir } from '~/utils/workDir';

const LAYOUT_PATH = 'app/_layout.tsx';

export async function transformExpoFrameworkReady(
  code: MagicString,
  moduleDeclarations: ModuleDeclaration[],
  path: string,
) {
  if (workbenchStore.framework.get()?.name !== 'expo') {
    return;
  }

  if (path !== LAYOUT_PATH) {
    return;
  }

  if (!(addWorkDir(useFrameworkReadyFilePath) in workbenchStore.files.get())) {
    await workbenchStore.writeFile(useFrameworkReadyFilePath, {
      type: 'file',
      content: useFrameworkReadyFileContent,
      isBinary: false,
      isStackBlitzBinary: false,
    });
  }

  insertImport(code, moduleDeclarations);
  insertHook(code, moduleDeclarations);
}

function insertImport(code: MagicString, moduleDeclarations: ModuleDeclaration[]) {
  const hasImport = moduleDeclarations.some(
    (declaration) =>
      declaration.type === 'import' &&
      declaration.module.path === '@/hooks/useFrameworkReady' &&
      declaration.namedImports?.some(({ specifier }) => specifier === 'useFrameworkReady'),
  );

  if (hasImport) {
    return;
  }

  const lastImport = moduleDeclarations.findLast((declaration) => declaration.type === 'import');

  // insert after the last import, or at the beginning if no imports
  const insertPos = lastImport?.endIndex || 0;
  const prefix = lastImport ? '\n' : '';
  code.insert(
    insertPos,
    prefix +
      renderESM({
        type: 'import',
        module: {
          path: '@/hooks/useFrameworkReady',
          quote: "'",
        },
        namedImports: [{ specifier: 'useFrameworkReady' }],
        startIndex: insertPos,
        endIndex: insertPos,
      }),
  );
}

function insertHook(code: MagicString, moduleDeclarations: ModuleDeclaration[]) {
  const defaultExport = moduleDeclarations.find((declaration) => declaration.type === 'export' && declaration.default);

  if (!defaultExport) {
    return;
  }

  const tokens = Array.from(lexer(code.str.slice(defaultExport.endIndex), { jsx: true }));

  const hasHook = tokens.some(
    (token, index) =>
      token.type === 'IdentifierName' &&
      token.value === 'useFrameworkReady' &&
      tokens[index + 1]?.type === 'Punctuator' &&
      tokens[index + 1]?.value === '(' &&
      tokens[index + 2]?.type === 'Punctuator' &&
      tokens[index + 2]?.value === ')',
  );

  if (hasHook) {
    return;
  }

  let position = defaultExport.endIndex;
  let functionStart = -1;
  let functionBodyStart = -1;
  let bracketCount = 0;
  let foundFirstBracket = false;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (functionStart === -1) {
      // look for export default function
      if (tokens[i]?.value === 'function') {
        functionStart = position;
      }
    } else if (token.value === '{') {
      if (!foundFirstBracket) {
        functionBodyStart = position + token.value.length;
        foundFirstBracket = true;
      }

      bracketCount++;
    } else if (token.value === '}') {
      bracketCount--;

      if (bracketCount === 0 && foundFirstBracket) {
        // we've found the closing bracket of the function body
        break;
      }
    }

    position += token.value.length;
  }

  if (functionBodyStart === -1) {
    return;
  }

  // insert at the start of the function body
  code.insert(functionBodyStart, '\n' + '  useFrameworkReady();');
}

const useFrameworkReadyFilePath = 'hooks/useFrameworkReady.ts';

const useFrameworkReadyFileContent = stripIndents`
  import { useEffect } from 'react';

  declare global {
    interface Window {
      frameworkReady?: () => void;
    }
  }

  export function useFrameworkReady() {
    useEffect(() => {
      window.frameworkReady?.()
    })
  }
`;
